import React, { useState } from 'react';
import { theme, alpha } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { useMatchJoinApplications } from '../hooks/useMatchJoinApplications';
import JoinApplicationThreadModal from './JoinApplicationThreadModal';
import ErrorBoundary from './ErrorBoundary';
import type { JoinApplication } from '@myfutbolapp/shared';

interface Props {
  isOpen: boolean;
  matchId: string;
  onClose: () => void;
  onUpdated?: () => void;
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
}

const MatchJoinApplicationsModal: React.FC<Props> = ({ isOpen, matchId, onClose, onUpdated }) => {
  const { isMobile } = useResponsive();
  const { rows, loading, error, busy, act } = useMatchJoinApplications(matchId, isOpen);
  const [chatFor, setChatFor] = useState<JoinApplication | null>(null);
  const [actError, setActError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleAct = async (app: JoinApplication, action: 'accept' | 'reject') => {
    setActError(null);
    if (action === 'reject') {
      const ok = window.confirm('¿Rechazar esta postulación?');
      if (!ok) return;
    }
    const res = await act(app.id, action);
    if (res?.error) {
      setActError(res.error);
    } else {
      onUpdated?.();
    }
  };

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 2000,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: isMobile ? 0 : 16,
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: theme.colors.surface,
            width: '100%',
            maxWidth: 640,
            borderRadius: isMobile ? '20px 20px 0 0' : 18,
            padding: '20px 22px 22px',
            maxHeight: isMobile ? '92vh' : '88vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <h2
              style={{
                fontFamily: theme.fonts.heading,
                color: theme.colors.text,
                margin: 0,
                fontSize: 18,
                fontWeight: 800,
                flex: 1,
              }}
            >
              Postulaciones públicas
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              style={{
                background: theme.colors.background,
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                fontSize: 18,
                cursor: 'pointer',
                color: theme.colors.textSecondary,
              }}
            >
              ✕
            </button>
          </div>

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: alpha('danger', '20'),
                border: `1px solid ${theme.colors.danger}`,
                borderRadius: 10,
                color: theme.colors.danger,
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          {actError && (
            <div
              style={{
                padding: '10px 12px',
                background: alpha('danger', '20'),
                border: `1px solid ${theme.colors.danger}`,
                borderRadius: 10,
                color: theme.colors.danger,
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {actError}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: 24, color: theme.colors.textSecondary }}>
              Cargando postulaciones…
            </div>
          ) : rows.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 32,
                background: theme.colors.background,
                borderRadius: 12,
                color: theme.colors.textSecondary,
              }}
            >
              No hay postulaciones pendientes.
            </div>
          ) : (
            rows.map((app) => {
              const st = app.applicant.userStats;
              const isBusy = busy.has(app.id);
              return (
                <div
                  key={app.id}
                  style={{
                    background: theme.colors.background,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: 12,
                    padding: 14,
                    marginBottom: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    {app.applicant.avatar ? (
                      <img
                        src={app.applicant.avatar}
                        alt=""
                        style={{ width: 44, height: 44, borderRadius: 22, objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 22,
                          background: alpha('primary', '22'),
                          color: theme.colors.primary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                        }}
                      >
                        {initials(app.applicant.name)}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: theme.colors.text, fontWeight: 700, fontSize: 15 }}>
                        {app.applicant.name} · ⭐ {app.applicant.rating}
                      </div>
                      {st && (
                        <div style={{ color: theme.colors.textSecondary, fontSize: 12 }}>
                          Partidos: {st.totalMatches ?? 0} · Victorias: {st.totalWins ?? 0}
                          {st.mvpCount ? ` · MVP×${st.mvpCount}` : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  {app.preferredPosition && (
                    <div style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                      Posición: {app.preferredPosition}
                    </div>
                  )}
                  {app.selfRatedSkill != null && (
                    <div style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                      Nivel declarado: {app.selfRatedSkill}
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: 10,
                      padding: 10,
                      background: theme.colors.surface,
                      borderRadius: 8,
                      color: theme.colors.text,
                      fontSize: 13,
                    }}
                  >
                    {app.message}
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={() => setChatFor(app)}
                      style={{
                        padding: '8px 12px',
                        background: 'transparent',
                        color: theme.colors.primary,
                        border: `1px solid ${theme.colors.primary}`,
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                    >
                      💬 Conversar
                    </button>
                    <div style={{ flex: 1 }} />
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => handleAct(app, 'reject')}
                      style={{
                        padding: '8px 14px',
                        background: '#b91c1c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        opacity: isBusy ? 0.6 : 1,
                      }}
                    >
                      Rechazar
                    </button>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => handleAct(app, 'accept')}
                      style={{
                        padding: '8px 14px',
                        background: theme.colors.success,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 800,
                        fontSize: 13,
                        cursor: isBusy ? 'not-allowed' : 'pointer',
                        opacity: isBusy ? 0.6 : 1,
                        fontFamily: theme.fonts.heading,
                      }}
                    >
                      Aceptar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {chatFor && (
        <ErrorBoundary
          scope="join-application-thread"
          fallback={(_err, reset) => (
            <div
              role="dialog"
              aria-modal="true"
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                zIndex: 2200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
              }}
              onClick={() => {
                reset();
                setChatFor(null);
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: theme.colors.surface,
                  borderRadius: 16,
                  padding: 24,
                  maxWidth: 420,
                  width: '100%',
                  color: theme.colors.text,
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
                <h3 style={{ margin: '0 0 8px', color: theme.colors.text }}>
                  No pudimos mostrar la conversación
                </h3>
                <p style={{ color: theme.colors.textSecondary, marginBottom: 16 }}>
                  Tu mensaje pudo haberse enviado igual. Probá cerrar y abrir el chat de
                  nuevo.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    reset();
                    setChatFor(null);
                  }}
                  style={{
                    padding: '10px 18px',
                    background: theme.colors.primary,
                    color: '#000',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        >
          <JoinApplicationThreadModal
            isOpen={Boolean(chatFor)}
            matchId={matchId}
            applicationId={chatFor.id}
            applicationStatus={chatFor.status}
            otherPartyLabel={chatFor.applicant.name}
            initialApplicationMessage={chatFor.message}
            chatRole="organizer"
            onClose={() => setChatFor(null)}
          />
        </ErrorBoundary>
      )}
    </>
  );
};

export default MatchJoinApplicationsModal;
